import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NextPage } from 'next';
import { useDispatch } from 'react-redux';
import { useAuthFlow, useInfinityLoader, useMagicLinkAuthProvider, useQueryParams } from 'hooks';

import { EventsSelectors } from 'store/selectors';
import { EventsActions } from 'store/actions';

import { UserHeader } from 'components/common/Header';
import EventsScreen from 'components/screens/EventsScreen';
import FilterPanel from 'components/filters/FilterPanel';
import EventTargetAgesFilter from 'components/filters/EventTargetAgesFilter';
import EventRootTypesFilter from 'components/filters/EventTypesFilter';
import EventTopicsFilter from 'components/filters/EventTopicsFilter';
import PriceFilter from 'components/filters/PriceFilter';
import OngoingOpportunityFilter from 'components/filters/OngoingOpportunityFilter';
import ClearFiltersButton from 'components/filters/ClearFiltersButton';
import SortFilter from 'components/filters/SortFilter';
import DatesFilter from 'components/filters/DatesFilter';
import { TabTitle } from 'components/common/TabTitle';
import { trackPage } from 'components/screens/ScrenTracker';
import EventSearchFilter from 'components/filters/EventSearchFilter';
import LocalMarketFilter from '../../components/filters/LocalMarketFilter';
import OrgTypeFilter from '../../components/filters/OrgTypeFilter';
import EventNeighborhoodsFilter from '../../components/filters/EventHeighborhoodsFilter';

const EventsPage: NextPage = () => {
  const dispatch = useDispatch();
  useAuthFlow();
  useMagicLinkAuthProvider();

  const handleToggleFavoriteEvent = useCallback(
    (eventId: any) => dispatch(EventsActions.toggleFavoriteEvent.request({ id: eventId })),
    [dispatch],
  );

  const [queryParams] = useQueryParams();

  const defaultQueryParams = useMemo(
    () => ({ page_size: 12, ordering: 'session_date', ...queryParams }),
    [queryParams],
  );

  const stateSelector = useMemo(() => EventsSelectors.upcomingEventsState(), []);
  const paginationSelector = useMemo(() => EventsSelectors.upcomingEventsPagination(), []);

  const [isInitialResetCompleted, setInitialResetCompleted] = useState(false);

  useEffect(() => {
    if (!isInitialResetCompleted) {
      dispatch(
        EventsActions.getUpcomingEvents.reset({
          params: { page_size: 12, ...queryParams },
        }),
      );
      setInitialResetCompleted(true);
    }
  }, [dispatch, isInitialResetCompleted, queryParams]);

  const {
    state: eventsState,
    setElementWrapper,
    setElementChild,
  } = useInfinityLoader({
    actions: EventsActions.getUpcomingEvents,
    stateSelector,
    paginationSelector,
    options: {
      defaultQueryParams,
      queryParams,
    },
  });

  useEffect(() => {
    setElementWrapper(window);
    setElementChild(document.documentElement);
    trackPage('Events Page');
  }, [setElementChild, setElementWrapper]);

  return (
    <>
      <TabTitle i18nKey="tab.events" ns="public" />
      <UserHeader hasSearch={false} />
      <EventsScreen
        events={eventsState.entities}
        isEventsFetching={eventsState.isFetching}
        onToggleFavoriteEvent={handleToggleFavoriteEvent}
        top={
          <FilterPanel
            search={<EventSearchFilter disabled={eventsState.isFetching} />}
            sort={<SortFilter disabled={eventsState.isFetching} />}
          >
            <ClearFiltersButton disabled={eventsState.isFetching} />
            <OrgTypeFilter disabled={eventsState.isFetching} name="organization_types" hasFullName />
            <EventRootTypesFilter disabled={eventsState.isFetching} />
            <EventTopicsFilter disabled={eventsState.isFetching} />
            <EventTargetAgesFilter disabled={eventsState.isFetching} />
            <PriceFilter disabled={eventsState.isFetching} />
            <LocalMarketFilter disabled={eventsState.isFetching || !!queryParams.neighborhood} />
            <DatesFilter disabled={eventsState.isFetching} />
            <OngoingOpportunityFilter disabled={eventsState.isFetching} />
            <EventNeighborhoodsFilter disabled={eventsState.isFetching} />
          </FilterPanel>
        }
      />
      <div style={{ paddingBottom: 64 }} />
    </>
  );
};

export default EventsPage;
